import React, { useEffect, useMemo, useState } from 'react';
import { useStyleThemeColors } from '../StyleEditor';
import { datasetsApi } from '../../lib/api';
import {
  inferFields, geometryCounts, featuresBbox, formatBbox, describeSource, crsSummary, datasetSummary, importSummary,
} from '../../lib/layer-info';
import { rasterInfoRows } from '../../lib/raster-api';

/**
 * Info tab: source, CRS / georef, extent, features and geometry types, the field list with
 * inferred types and sample values, and the metadata of the dataset behind the layer
 * (raw survey, QC, raster, tiles) with a link to its cards. Read-only.
 */

function Section({ title, children }) {
  const C = useStyleThemeColors();
  return (
    <div>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: C.text3, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function Rows({ rows }) {
  const C = useStyleThemeColors();
  return (
    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ color: C.text3, paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
            <td style={{ color: C.text1, wordBreak: 'break-word' }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function boundsToBbox(b) {
  if (!Array.isArray(b)) return null;
  if (Array.isArray(b[0])) return [b[0][0], b[0][1], b[1][0], b[1][1]];
  return b.length === 4 ? b : null;
}

export default function InfoTab({ layer, navigate }) {
  const C = useStyleThemeColors();
  const [dataset, setDataset] = useState(null);
  const [loadError, setLoadError] = useState('');
  const datasetId = layer.datasetId || '';

  useEffect(() => {
    setDataset(null);
    setLoadError('');
    if (!datasetId || !datasetsApi?.get) return undefined;
    let active = true;
    datasetsApi.get(datasetId)
      .then((res) => { if (active) setDataset(res.data); })
      .catch(() => { if (active) setLoadError('Could not load the dataset record.'); });
    return () => { active = false; };
  }, [datasetId]);

  const fields = useMemo(() => inferFields(layer.features), [layer.features]);
  const geoms = useMemo(() => geometryCounts(layer.features), [layer.features]);
  const bbox = useMemo(() => boundsToBbox(layer.bounds) || featuresBbox(layer.features), [layer.bounds, layer.features]);
  const summary = useMemo(() => datasetSummary(dataset), [dataset]);
  const source = describeSource(layer);

  const sourceRows = [['Source', source.label]];
  if (source.detail) sourceRows.push([source.kind === 'service' ? 'Service URL' : source.kind === 'file' ? 'File' : 'Reference', source.detail]);
  if (layer.method) sourceRows.push(['Method', layer.method]);
  const raster = dataset?.metadata?.raster;

  return (
    <>
      <Section title="Source"><Rows rows={sourceRows} /></Section>
      <Section title="Coordinate system"><Rows rows={crsSummary(layer, summary.georef)} /></Section>
      {layer.importInfo && <Section title="Import"><Rows rows={importSummary(layer.importInfo)} /></Section>}
      <Section title="Extent">
        <Rows rows={[['Bounding box', bbox ? `${formatBbox(bbox)} (W, S, E, N)` : 'Unknown']]} />
      </Section>
      {layer.kind !== 'raster' && (
        <Section title="Features">
          <Rows rows={[
            ['Count', Number(layer.featureCount).toLocaleString()],
            ['Geometry', Object.keys(geoms).length ? Object.entries(geoms).map(([t, n]) => `${t} (${n.toLocaleString()})`).join(', ') : (layer.geomType || 'unknown')],
          ]} />
        </Section>
      )}
      {layer.kind === 'vector' && (
        <Section title={`Fields (${fields.length})`}>
          {fields.length === 0 ? <div style={{ fontSize: 11, color: C.text3 }}>No attributes.</div> : (
            <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }} data-testid="field-table">
              <thead>
                <tr style={{ color: C.text3, textAlign: 'left' }}><th>Name</th><th>Type</th><th>Samples</th></tr>
              </thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.name} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ color: C.text0, paddingRight: 6 }}>{f.name}</td>
                    <td style={{ color: C.text2, paddingRight: 6 }}>{f.type}</td>
                    <td style={{ color: C.text2, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.samples.join(' | ')}>{f.samples.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
      {datasetId && (
        <Section title="Dataset">
          {loadError && <div style={{ fontSize: 11, color: C.red }} role="alert">{loadError}</div>}
          {!dataset && !loadError && <div style={{ fontSize: 11, color: C.text3 }}>Loading dataset…</div>}
          {dataset && <Rows rows={summary.rows} />}
          {raster && <div style={{ marginTop: 6 }}><Rows rows={rasterInfoRows(raster)} /></div>}
          {summary.links.map((l) => (
            <button key={l.to} onClick={() => navigate?.(l.to)}
              style={{ marginTop: 8, fontSize: 11, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', color: C.cyan, background: C.accentBg, border: `1px solid ${C.accentBorder}` }}>
              {l.label}
            </button>
          ))}
        </Section>
      )}
    </>
  );
}
